// CutIn.tsx — FxOverlay (P2 design §7.5): 発声帯 / 役満カットイン / 配牌バナー / スキップ用
// 透明タップ層を描画する overlay。SoloMahjong が position:relative なルート直下に置く。
// リザルトの段階開示モーダル本体はここでは描かない (fx.resultStage を integrate のモーダルが読む)。
//
// スタイル契約: クラスは theme.css のもの (.fx-band / .fx-cutin-overlay / .fx-cutin-text /
// .fx-cutin-art / .fx-band-in / .fx-pop-in / .fx-cutin-in / .visually-hidden) を参照するだけ。
// inline は配置 (top / 幅) とトーン色 (--fx-band-color) の注入のみ。z は D2 トークン。
// ルート自身は z-index を持たない — 子 (band=50 / cutin=70) が他層 (modal=60) と正しく前後する。
import type { CSSProperties } from 'react';
import type { BandTone, FxState } from './fx';

// 帯トーン → theme.css の色トークン (D2/§3.1)。
const TONE_VAR: Record<BandTone, string> = {
  riichi: '--band-riichi',
  openRiichi: '--band-open-riichi',
  infiniteRiichi: '--band-infinite-riichi',
  pon: '--band-pon',
  chii: '--band-chii',
  kan: '--band-kan',
  ron: '--band-ron',
  tsumo: '--band-tsumo',
  ryuukyoku: '--band-ryuukyoku',
};

// 全幅の横帯 (A5 立直 / A12 流局) か、席寄りの発声帯 (A6 鳴き・和了宣言) か。
const FULL_TONES: ReadonlySet<BandTone> = new Set(['riichi', 'openRiichi', 'infiniteRiichi', 'ryuukyoku']);

// 役満カットイン用 sparkle 配置 (P2 §7・決め打ち・画面相対 %)。delay は staggered。
// 見た目/twinkle/drift は theme.css の .fx-cutin-sparkle が持つ。ここは座標と遅延の注入のみ。
const CUTIN_SPARKLES: ReadonlyArray<{ left: string; top: string; delay: number }> = [
  { left: '22%', top: '34%', delay: 120 },
  { left: '78%', top: '30%', delay: 260 },
  { left: '34%', top: '64%', delay: 0 },
  { left: '68%', top: '60%', delay: 340 },
  { left: '14%', top: '52%', delay: 200 },
  { left: '86%', top: '50%', delay: 420 },
];

// 発声帯の縦位置: 呼んだ席寄り (A6)。humanSeat は常に 0 (tableGame の HUMAN 固定) なので、
// 自分=手元側 (下)、他家=卓向こう側 (上) に寄せる。横位置までは席→画面写像を持たないため
// 中央 (margin auto) — 席ごとの左右寄せが欲しくなったら integrate が SeatBoard の向き情報で拡張する。
function bandTop(tone: BandTone, seat: number | null): string {
  if (FULL_TONES.has(tone)) return '42%';
  return seat === 0 ? '62%' : '28%';
}

const bannerStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: '36%',
  width: 'fit-content',
  marginInline: 'auto',
  padding: '10px 26px',
  background: 'var(--surface-modal)',
  border: '1px solid var(--border-gold)',
  borderRadius: 'var(--r-md)',
  boxShadow: 'var(--shadow-2)',
  color: 'var(--gold-300)',
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--fs-xl)',
  fontWeight: 700,
  letterSpacing: '0.2em',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  zIndex: 'var(--z-band)' as CSSProperties['zIndex'],
  pointerEvents: 'none',
};

export function FxOverlay({ fx, onSkip, ariaLines = [] }: {
  fx: FxState;
  onSkip: () => void;
  /** useTableFx の ariaLines (直近6件) — aria-live region に流す。省略時は region 空。 */
  ariaLines?: string[];
}): JSX.Element {
  const { band, cutin, dealing } = fx;
  const skippable = band !== null || cutin !== null || dealing !== null;

  return (
    // ルートは当たり判定なし (pointerEvents none) — 盤面操作を遮らない。タップ層だけ opt-in。
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {band !== null && (() => {
        const full = FULL_TONES.has(band.tone);
        const style = {
          top: bandTop(band.tone, band.seat),
          '--fx-band-color': `var(${TONE_VAR[band.tone]})`,
          // A6: 発声帯は幅 min(60%,360px)・明朝 28px。transform は keyframe が専有するので
          // 中央寄せは margin auto (left/right:0 は .fx-band 既定)。
          ...(full ? {} : { maxWidth: 'min(60%, 360px)', marginInline: 'auto', '--fx-band-fs': '28px' }),
        } as CSSProperties;
        return (
          // key=id で band ごとに再 mount → 入場アニメが毎回走る。全幅帯は横スライド (A5)、
          // 発声帯はポップ (A6)。退場は blocking 満了の unmount (reduced では CSS 側で静止)。
          <div key={band.id} className={full ? 'fx-band fx-band-in' : 'fx-band fx-pop-in'} style={style}>
            {band.text}
          </div>
        );
      })()}

      {cutin !== null && (
        // 層順 (P2 §7): rays → flash → sparkle×6 → art → text。装飾層は aria-hidden。
        // 情報は文字と既存 aria-live region が運ぶ。色/アニメ定義はすべて theme.css。
        <div key={cutin.id} className="fx-cutin-overlay">
          {/* 1. 放射ゴールドレイ (burst-in → subtle 回転)。 */}
          <div className="fx-cutin-rays" aria-hidden="true" />

          {/* 2. 着弾フラッシュ (高速 one-shot)。 */}
          <div className="fx-cutin-flash" aria-hidden="true" />

          {/* 3. 金の粒 ×6 — left/top/--spark-delay を inline 注入 (staggered twinkle)。 */}
          {CUTIN_SPARKLES.map((s, i) => (
            <div
              key={`spark${i}`}
              className="fx-cutin-sparkle"
              aria-hidden="true"
              style={{
                left: s.left,
                top: s.top,
                ['--spark-delay' as string]: `${s.delay}ms`,
              } as CSSProperties}
            />
          ))}

          {/* 4. キャラ絵スロット — 構造のみ。画像は入れない (§0-7)。 */}
          <div className="fx-cutin-art" />

          {/* 5. 役名 — スラム-in + シマーは theme.css の .fx-cutin-text に集約 (fx-cutin-in は外す)。
                 複数役満は 250ms ずらし縦積み (A9)。fill-mode both で delay 待ち中も from 状態を維持。 */}
          {cutin.names.map((name, i) => (
            <div
              key={`${cutin.id}:${i}`}
              className="fx-cutin-text"
              style={{ animationDelay: `${i * 250}ms`, animationFillMode: 'both' }}
            >
              {name}
            </div>
          ))}
        </div>
      )}

      {dealing !== null && (
        // A11①: 中央バナー「東1局 2本場」。保持/消灯は fx.ts の dealIntro ジョブが管理する。
        <div key={dealing.id} className="fx-pop-in" style={bannerStyle}>
          {dealing.banner}
        </div>
      )}

      {skippable && (
        // スキップ用透明タップ層 — 演出が出ている間だけ。カットイン (z=70) より手前に置き、
        // 1 タップで現行 blocking + リザルト段階を最終形へ早送り (§7.2 skip)。
        // リザルトモーダル表示中のスキップはモーダル側 (integrate) が skip() を呼ぶ。
        <div
          aria-hidden="true"
          onPointerDown={onSkip}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 'var(--z-cutin)' as CSSProperties['zIndex'],
            pointerEvents: 'auto',
            cursor: 'pointer',
            background: 'transparent',
          }}
        />
      )}

      {/* ARIA: 演出と等価の情報をテキストで (§7.5)。視覚演出を見ない読者にも帯・宣言が届く。 */}
      <div aria-live="polite" className="visually-hidden">
        {ariaLines.map((line, i) => (
          <div key={`${i}:${line}`}>{line}</div>
        ))}
      </div>
    </div>
  );
}
